import { FC, useRef, useEffect, useMemo } from "react";
import Highcharts from "highcharts";
import { Box } from "@mui/material";
import moment from "moment";
import Stack from "@mui/material/Stack";
import { LegendContent } from "./LegendContent";
import { metricLabel } from "../../../../utils/helpers/metricLabel";
import { RightsizingRecommendationsChartData } from "../../../../utils/types/types";
import { ChartErrorBoundary } from "@/storybook/c8r-highcharts/ChartErrorBoundary";
import {
  formatDate,
  dateFromUtcByTimeZone,
} from "@/utils/helpers/date-time/datetime-format";
import { rightsizingChartTooltipOptions } from "@/components/pages/recommendations/components/rightsizing-recommendations/components/table/subrow/charts/helpers/rightsizingChartTooltipOptions";
import { generateRightsizingHighChartData } from "@/components/pages/recommendations/components/rightsizing-recommendations/components/table/subrow/charts/helpers/generateRightsizingHighChartData";

interface RightsizingRecommendationsChartProps {
  data: RightsizingRecommendationsChartData[];
  currentType: string;
  recommendedType: string;
  metric: "average" | "maximum";
}

const yAxisCpuTicks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const RightsizingRecommendationsChartContent: FC<
  RightsizingRecommendationsChartProps
> = ({ data, currentType, recommendedType, metric }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const chartData = useMemo(() => {
    return generateRightsizingHighChartData(
      data,
      currentType,
      recommendedType,
      metric,
    );
  }, [data, currentType, recommendedType, metric]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    // @ts-expect-error  | Something wrong with types, this is waiting for "Options" but it should be an "Element"
    const chart = Highcharts.chart(chartRef.current, {
      chart: { type: "area", height: 380 },
      title: { text: "" },
      xAxis: {
        categories: chartData.categories,
        crosshair: { width: 1, color: "#000", dashStyle: "ShortDot" },
        labels: {
          formatter: function () {
            return formatDate(
              dateFromUtcByTimeZone(`${this.value}`) || moment(),
              { type: "dateTime" },
            );
          },
          style: { fontSize: "10px" },
        },
        tickInterval: Math.max(1, Math.floor(data.length / 7) - 1),
      },
      yAxis: {
        title: { text: "" },
        tickPositions: yAxisCpuTicks,
        labels: { style: { fontSize: "8px" } },
      },
      tooltip: rightsizingChartTooltipOptions,
      plotOptions: {
        area: { marker: { enabled: false } },
        series: { animation: false },
      },
      legend: { enabled: false },
      series: chartData.series,
      credits: { enabled: false },
    });

    return () => {
      chart.destroy();
    };
  }, [chartData, data, currentType, recommendedType, metric]);

  return (
    <Stack>
      <Box ref={chartRef} width="100%" height={400} />
      <LegendContent
        currentType={currentType}
        metric={metricLabel(metric)}
        recommendedType={recommendedType}
      />
    </Stack>
  );
};

export const RightsizingRecommendationsChart: FC<
  RightsizingRecommendationsChartProps
> = (props) => {
  return (
    <ChartErrorBoundary>
      <RightsizingRecommendationsChartContent {...props} />
    </ChartErrorBoundary>
  );
};
